import type { ContactResult } from "../../../lib/contacts/types";
import type { LocationResult } from "../../../lib/contacts/router";
import { TrainingContactResultCard } from "@/components/lead/sections/TrainingContactResultCard";

export function TrainingAskSourcesContacts(props: any) {
  const { result, answerMode, locationCards, locationError, contacts, contactsError, contactsWhy, onContactTagClick } = props;
  const contactTopics: string[] = Array.from(
    new Set<string>(
      (contacts || [])
        .flatMap((c: ContactResult) =>
          Array.isArray(c.topics) ? c.topics.map((t: string) => String(t || "").trim()) : []
        )
        .filter((t: string): t is string => t.length > 0)
    )
  ).slice(0, 10);
  const showContactsTagHints = answerMode === "contacts" && result.hits.length === 0 && contacts.length > 0;
  return (
    <>
      <div className="mt-3">
        <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">{showContactsTagHints ? "Contacts lane tags" : "Sources"}</div>
        {showContactsTagHints ? (
          <div>
            {contactTopics.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {contactTopics.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => onContactTagClick?.(tag)}
                    className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200 hover:bg-gray-200"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="text-xs text-gray-500 mt-2">
              Tip: open the Contacts tab and search with filters like <span className="font-mono">tag:marketing</span> or <span className="font-mono">tag:primary_advertising_contact</span>.
            </div>
          </div>
        ) : result.hits.length === 0 ? (
          <div className="text-xs text-gray-500">No sources found.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {result.hits.map((h: any) =>
              h.url ? (
                <a key={h.id} href={h.url} target="_blank" rel="noreferrer" className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200 hover:bg-gray-200" title={h.excerpt || h.title}>
                  {h.title}{h.docType ? ` · ${h.docType}` : ""}{h.pricingGrounded ? " • pricing-grounded" : ""}{h.freshnessState && h.freshnessState !== "unknown" ? ` • ${h.freshnessState}` : ""}
                </a>
              ) : (
                <span key={h.id} className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200" title={h.excerpt || h.title}>
                  {h.title}{h.docType ? ` · ${h.docType}` : ""}{h.pricingGrounded ? " • pricing-grounded" : ""}{h.freshnessState && h.freshnessState !== "unknown" ? ` • ${h.freshnessState}` : ""}
                </span>
              )
            )}
          </div>
        )}
      </div>

      {result.hits.some((h: any) => h.excerpt) ? (
        <details className="mt-3 text-xs text-gray-600">
          <summary className="cursor-pointer text-blue-600">Show excerpts</summary>
          <div className="mt-2 space-y-2">
            {result.hits.filter((h: any) => h.excerpt).map((h: any) => (
              <div key={h.id} className="border border-gray-200 rounded-xl p-2">
                <div className="font-semibold text-gray-800">{h.title}</div>
                <div className="text-[11px] text-gray-500">{h.docType ? `doc:${h.docType}` : "doc:unknown"}{h.freshnessState ? ` • freshness:${h.freshnessState}` : ""}{h.pricingGrounded ? " • pricing-grounded" : ""}</div>
                <div className="text-gray-600">{h.excerpt}</div>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      {locationCards.length > 0 || locationError ? (
        <div className="mt-4">
          <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Location info</div>
          {locationError ? (
            <div className="text-xs text-gray-500">Couldn’t load location info right now. ({locationError})</div>
          ) : (
            <div className="space-y-2">
              {locationCards.slice(0, 3).map((r: LocationResult, idx: number) => {
                const addr = [r.address, r.city, r.state, r.zip].filter(Boolean).join(", ");
                const label = `${r.location || "Unknown Location"}${r.company ? ` — ${r.company}` : ""}`;
                return (
                  <div key={`${r.location}-${idx}`} className="border border-gray-200 rounded-xl p-2 bg-gray-50">
                    <div className="text-sm font-semibold text-gray-900">{label}</div>
                    <div className="mt-1 text-xs text-gray-700">{addr ? <div>Address: {addr}</div> : null}{r.office ? <div>Office: {r.office}</div> : null}{r.email ? <div>Email: {r.email}</div> : null}{r.salespersons ? <div>Salespersons: {r.salespersons}</div> : null}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      <div className="mt-4">
        <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Recommended contacts</div>
        {contactsError ? (
          <div className="text-xs text-gray-500">Couldn’t load contacts right now. ({contactsError})</div>
        ) : contacts.length === 0 ? (
          <div className="text-xs text-gray-500">{contactsWhy ? "No matching contacts found." : "Ask “Who do I talk to about …” to pull contacts, or contacts will appear automatically for low-confidence policy answers."}</div>
        ) : (
          <div className="space-y-2">
            {contactsWhy ? <div className="text-xs text-gray-600">{contactsWhy === "direct" ? "Based on your question, here are the best contacts to start with." : "This answer may require confirmation. Here are the most relevant authoritative contacts."}</div> : null}
            {contacts.map((c: ContactResult, idx: number) => {
              const recommended = contacts.find((x: ContactResult) => x.recommended) || contacts[0];
              const logQuery = props.resultQuery || props.lastQuery || props.query.trim() || "";
              return (
                <TrainingContactResultCard
                  key={`${c.tab}-${idx}-${c.name || c.location || "x"}`}
                  c={c}
                  organizationId={props.scopedOrganizationId}
                  logQuery={logQuery}
                  recommended={recommended}
                />
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
